import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Archive,
  Calendar,
  CheckCircle2,
  FileText,
  Loader2,
  RefreshCw,
  Sparkles,
  User,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  autoMatchViClient,
  fetchViClientSnapshot,
  fetchViIntegrationStatus,
  manualLinkViClient,
} from "../../../api/vitalInsightsIntegration";
import { normalizeViAgencyDashboardUrl } from "../../../utils/vitalInsightsUrl";
import {
  CARE_DIMENSIONS,
  formatCareLevel,
  formatVisitDate,
} from "./careSnapshotDisplay";
import "./VitalInsightsSection.css";

const VI_LOGO_SRC = "/images/VitalInsights.png";

function resolveCandidateId(candidate) {
  return String(
    candidate?.hearthClientId ||
      candidate?.hearth_client_id ||
      candidate?.id ||
      candidate?._id ||
      "",
  ).trim();
}

function normalizeMatchCandidates(candidates) {
  return (candidates || [])
    .map((row) => {
      const hearthClientId = resolveCandidateId(row);
      if (!hearthClientId) return null;
      return {
        hearthClientId,
        name: String(row?.name || "").trim(),
        date_of_birth: row?.date_of_birth || row?.dateOfBirth || null,
      };
    })
    .filter(Boolean);
}

function WellnessGrid({ snapshot = {} }) {
  return (
    <div className="vi-wellness-grid">
      {CARE_DIMENSIONS.map((dim) => {
        const level = formatCareLevel(snapshot?.[dim.id]);
        return (
          <div key={dim.id} className={`vi-wellness-card vi-tone-${level.tone}`}>
            <span className="vi-wellness-icon" aria-hidden="true">
              {dim.icon}
            </span>
            <span className="vi-wellness-title">{dim.title}</span>
            <strong className="vi-wellness-label">{level.label}</strong>
          </div>
        );
      })}
    </div>
  );
}

function ReportCard({ visit, emptyLabel = "No reports yet." }) {
  if (!visit) {
    return (
      <div className="vi-report-empty">
        <FileText size={28} strokeWidth={1.5} />
        <p>{emptyLabel}</p>
      </div>
    );
  }

  const score = visit.wellness_score;

  return (
    <article className="vi-report-card">
      <header className="vi-report-header">
        <div className="vi-report-header-main">
          <div className="vi-report-avatar" aria-hidden="true">
            <User size={18} />
          </div>
          <div>
            <h3 className="vi-report-caregiver">
              {visit.caregiver_name || "Caregiver"}
            </h3>
            <p className="vi-report-date">
              <Calendar size={13} />
              {formatVisitDate(visit.visit_date)}
            </p>
          </div>
        </div>
        <div className="vi-report-badges">
          {score != null ? (
            <span className="vi-score-badge">{score}/100</span>
          ) : null}
          {visit.flagged ? <span className="vi-flag-badge">Flagged</span> : null}
        </div>
      </header>

      {visit.ai_summary ? (
        <section className="vi-ai-summary">
          <div className="vi-ai-summary-head">
            <span className="vi-ai-label">
              <Sparkles size={14} />
              AI summary
            </span>
            <span className="vi-ai-brand">ALBERTai</span>
          </div>
          <p>{visit.ai_summary}</p>
        </section>
      ) : null}

      <section className="vi-report-block">
        <h4 className="vi-block-title">Wellness dimensions</h4>
        <WellnessGrid snapshot={visit.care_snapshot} />
      </section>

      {Array.isArray(visit.tasks_completed) && visit.tasks_completed.length ? (
        <section className="vi-report-block">
          <h4 className="vi-block-title">Tasks completed</h4>
          <ul className="vi-task-list">
            {visit.tasks_completed.map((task) => (
              <li key={task}>
                <CheckCircle2 size={15} />
                <span>{task}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </article>
  );
}

function ArchiveList({ visits, selectedVisitId, onSelect, latestVisitId }) {
  if (!visits.length) {
    return <p className="vi-muted">No archived reports.</p>;
  }

  return (
    <div className="vi-archive-list">
      {visits.map((visit) => {
        const isActive = String(selectedVisitId) === String(visit.id);
        const isLatest = String(latestVisitId) === String(visit.id);
        return (
          <button
            key={visit.id}
            type="button"
            className={`vi-archive-item${isActive ? " active" : ""}`}
            onClick={() => onSelect(String(visit.id))}
          >
            <div className="vi-archive-item-main">
              <strong>{formatVisitDate(visit.visit_date)}</strong>
              <span>{visit.caregiver_name || "Caregiver"}</span>
            </div>
            <div className="vi-archive-item-meta">
              {isLatest ? <em className="vi-archive-latest">Latest</em> : null}
              {visit.wellness_score != null ? (
                <span className="vi-archive-score">{visit.wellness_score}</span>
              ) : null}
              {visit.flagged ? <em className="vi-archive-flag">Flagged</em> : null}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default function VitalInsightsSection({
  dashboardUrl,
  enabled = false,
  hearthAgencyId = "",
  clientId = "",
  clientName = "",
}) {
  const fallbackUrl = normalizeViAgencyDashboardUrl(dashboardUrl);
  const [integration, setIntegration] = useState({
    enabled: Boolean(enabled),
    hearthAgencyId: String(hearthAgencyId || "").trim(),
    dashboardUrl: fallbackUrl,
    locationName: "",
  });
  const linked =
    Boolean(integration.enabled) && Boolean(String(integration.hearthAgencyId || "").trim());
  const [statusLoading, setStatusLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [linkingCandidateId, setLinkingCandidateId] = useState("");
  const [error, setError] = useState("");
  const [matchMessage, setMatchMessage] = useState("");
  const [activeTab, setActiveTab] = useState("latest");
  const [snapshot, setSnapshot] = useState(null);
  const [selectedVisitId, setSelectedVisitId] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [showRelinkPanel, setShowRelinkPanel] = useState(false);

  const visits = snapshot?.visits || [];

  const latestVisit = useMemo(() => {
    if (!visits.length) return snapshot?.latest || null;
    if (snapshot?.latest?.id) {
      return visits.find((v) => String(v.id) === String(snapshot.latest.id)) || visits[0];
    }
    return visits[0];
  }, [snapshot, visits]);

  const latestVisitId = latestVisit?.id ? String(latestVisit.id) : "";

  const selectedVisit = useMemo(() => {
    if (!visits.length) return null;
    if (selectedVisitId) {
      return visits.find((v) => String(v.id) === String(selectedVisitId)) || visits[0];
    }
    return visits[0];
  }, [visits, selectedVisitId]);

  const chartData = useMemo(() => {
    return (snapshot?.wellness_trend || [])
      .filter((row) => row.wellness_score != null)
      .map((row) => ({
        label: new Date(row.date).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }),
        score: row.wellness_score,
      }));
  }, [snapshot]);

  const loadIntegrationStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const status = await fetchViIntegrationStatus();
      setIntegration({
        enabled: Boolean(status?.enabled),
        hearthAgencyId: String(status?.hearthAgencyId || "").trim(),
        dashboardUrl: normalizeViAgencyDashboardUrl(
          status?.dashboardUrl || dashboardUrl,
        ),
        locationName: String(status?.locationName || "").trim(),
      });
    } catch (_err) {
      setIntegration({
        enabled: Boolean(enabled),
        hearthAgencyId: String(hearthAgencyId || "").trim(),
        dashboardUrl: fallbackUrl,
        locationName: "",
      });
    } finally {
      setStatusLoading(false);
    }
  }, [dashboardUrl, enabled, fallbackUrl, hearthAgencyId]);

  useEffect(() => {
    loadIntegrationStatus();
  }, [loadIntegrationStatus]);

  const loadSnapshot = useCallback(
    async ({ refresh = false } = {}) => {
      if (!linked || !clientId) return;
      setDataLoading(true);
      setError("");
      try {
        const result = await fetchViClientSnapshot(clientId, { refresh, limit: 20 });
        setSnapshot({
          ...result,
          matchCandidates: normalizeMatchCandidates(result?.matchCandidates),
        });
        if (result?.latest?.id) {
          setSelectedVisitId(String(result.latest.id));
        } else if (result?.visits?.[0]?.id) {
          setSelectedVisitId(String(result.visits[0].id));
        }
        setLastRefreshedAt(new Date());
        setShowRelinkPanel(false);
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Could not load Vital Insights visit data",
        );
      } finally {
        setDataLoading(false);
      }
    },
    [clientId, linked],
  );

  useEffect(() => {
    if (!statusLoading && linked && clientId) {
      loadSnapshot();
    }
  }, [statusLoading, linked, clientId, loadSnapshot]);

  const applyLinkState = useCallback((link) => {
    if (!link) return;
    setSnapshot((prev) => ({
      ...(prev || {}),
      linked: Boolean(link.hearthClientId),
      matchStatus: link.matchStatus || "unmatched",
      matchCandidates: normalizeMatchCandidates(link.matchCandidates),
      hearthClientId: link.hearthClientId || null,
      hearthClientName: link.hearthClientName || "",
    }));
  }, []);

  const runAutoMatch = async ({ forceRelink = false } = {}) => {
    if (!clientId) {
      setMatchMessage("");
      setError("Client id is missing — reload this page and try again.");
      return;
    }
    const forceSearch = forceRelink || showRelinkPanel;
    setMatchLoading(true);
    setMatchMessage("");
    setError("");
    try {
      const matchResult = await autoMatchViClient(clientId, { force: forceSearch });
      const link = matchResult?.link;
      applyLinkState(link);

      const linkedStatuses = ["matched", "manual"];
      if (
        forceSearch &&
        link?.hearthClientId &&
        linkedStatuses.includes(String(link?.matchStatus || "")) &&
        !(link?.matchCandidates || []).length
      ) {
        setMatchMessage("");
        setError(
          `Could not list other VI profiles — API still linked to ${link.hearthClientName || "the old profile"}. Deploy the latest Albert backend (force rematch) and Hearth API, then use Change link again.`,
        );
        return;
      }

      if (
        !forceSearch &&
        link?.hearthClientId &&
        linkedStatuses.includes(String(link?.matchStatus || ""))
      ) {
        setMatchMessage(
          `Matched to ${link.hearthClientName || "Vital Insights client"}. Loading visits…`,
        );
        await loadSnapshot({ refresh: true });
        setMatchMessage("");
        return;
      }

      if (link?.matchCandidates?.length) {
        setMatchMessage(
          link.matchCandidates.length > 1
            ? "Multiple VI clients found — pick the correct person below."
            : "Possible match found — confirm below (date of birth may differ).",
        );
        return;
      }

      setMatchMessage("");
      const agencyCount = link?.agencyClientCount;
      const configuredAgencyId = String(
        link?.hearthAgencyId || integration.hearthAgencyId || "",
      ).trim();
      const agencyHint =
        agencyCount === 0
          ? ` MyAlbert found 0 clients for VI agency ID ${configuredAgencyId || "(missing)"}. In Super Admin → Family Vital Insights, paste the Mongo _id from VI Super Admin for the agency where visits are logged (not the Albert agency id). If this loved one was created before agency linking, open their VI profile → Basic info → set Home care agency, or run the Hearth link script on the server.`
          : agencyCount != null
            ? ` MyAlbert searched ${agencyCount} VI client(s) under VI agency ${configuredAgencyId}.`
            : "";
      setError(
        `No Vital Insights client found for ${clientName || "this person"}.${agencyHint} Confirm date of birth matches MyAlbert.ai.`,
      );
    } catch (err) {
      setMatchMessage("");
      const isNetwork =
        !err?.response &&
        (err?.message === "Network Error" || err?.code === "ERR_NETWORK");
      const isTimeout = err?.code === "ECONNABORTED";
      setError(
        isTimeout
          ? "Match timed out — Albert API or Family Vital Insights API may be slow. Retry in a minute; if it keeps failing, check ALBERT_VI_SSO_SECRET and HEARTH_API_URL on the Albert server."
          : isNetwork
          ? "Could not reach the MyAlbert API. In DevTools → Console, look for a CORS message; if present, redeploy Albert backend (CORS) and Albert UI (latest match client). Otherwise check api.myalbertai.com is up."
          : err?.response?.data?.message ||
              err?.message ||
              "Match failed — check that Albert backend and VI API are deployed with ALBERT_VI_SSO_SECRET configured.",
      );
    } finally {
      setMatchLoading(false);
    }
  };

  const pickCandidate = async (candidate) => {
    const hearthClientId = resolveCandidateId(candidate);
    if (!clientId) {
      setError("Client id is missing — reload this page and try again.");
      return;
    }
    if (!hearthClientId) {
      setError(
        "Could not link — Vital Insights client id is missing from this match. Click Refresh, then Find again.",
      );
      return;
    }
    setLinkingCandidateId(hearthClientId);
    setMatchMessage("Linking to Vital Insights…");
    setError("");
    try {
      const linkResult = await manualLinkViClient(
        clientId,
        hearthClientId,
        candidate.name || "",
      );
      applyLinkState(linkResult?.link);
      setMatchMessage("Linked. Loading visit data…");
      await loadSnapshot({ refresh: true });
      setMatchMessage("");
    } catch (err) {
      setMatchMessage("");
      setError(err?.response?.data?.message || err?.message || "Link failed");
    } finally {
      setLinkingCandidateId("");
    }
  };

  const clientLinked = Boolean(snapshot?.linked && snapshot?.hearthClientId);
  const matchCandidates = snapshot?.matchCandidates || [];
  const showMatchCandidates =
    matchCandidates.length > 0 && (!clientLinked || showRelinkPanel);
  const matchBusy = matchLoading || Boolean(linkingCandidateId);
  const personFound = clientLinked || showMatchCandidates;

  return (
    <div className="vi-section vi-section-wide">
      <div className="vi-hero">
        <div className="vi-logo-wrap">
          <img src={VI_LOGO_SRC} alt="Family Vital Insights" className="vi-logo" />
        </div>
        <div className="vi-hero-body">
          <div className="vi-hero-top">
            <p className="vi-eyebrow">MyAlbert.ai ↔ Family Vital Insights</p>
            <div className="vi-pill-row">
              <span
                className={`vi-pill ${
                  statusLoading
                    ? "vi-pill-loading"
                    : linked
                      ? "vi-pill-connected"
                      : "vi-pill-disconnected"
                }`}
              >
                {statusLoading ? "…" : linked ? "Connected" : "Disconnected"}
              </span>
              {personFound ? (
                <span className="vi-pill vi-pill-found">Found</span>
              ) : null}
            </div>
          </div>
          <h2 className="vi-title">Family Vital Insights</h2>
          <p className="vi-subtitle">
            Care reports from Vital Insights — AI summaries, wellness, and tasks.
            {clientName ? (
              <>
                {" "}
                · <strong>{clientName}</strong>
              </>
            ) : null}
          </p>
        </div>
      </div>

      {linked && clientId ? (
        <div className="vi-data-panel">
          {clientLinked && !showRelinkPanel ? (
            <div className="vi-toolbar">
              <div className="vi-tabs">
                <button
                  type="button"
                  className={activeTab === "latest" ? "active" : ""}
                  onClick={() => setActiveTab("latest")}
                >
                  <FileText size={14} />
                  Latest report
                </button>
                <button
                  type="button"
                  className={activeTab === "archive" ? "active" : ""}
                  onClick={() => setActiveTab("archive")}
                >
                  <Archive size={14} />
                  Archive
                  {visits.length ? (
                    <span className="vi-tab-count">{visits.length}</span>
                  ) : null}
                </button>
              </div>
              <div className="vi-toolbar-actions">
                {snapshot?.hearthClientId ? (
                  <span className="vi-link-meta" title="VI Mongo client id used for care reports">
                    VI link: {snapshot.hearthClientName || "Bill"} ·{" "}
                    <code>{String(snapshot.hearthClientId).slice(-8)}</code>
                    {snapshot?.stats?.last_visit_at ? (
                      <>
                        {" "}
                        · last visit{" "}
                        {formatVisitDate(snapshot.stats.last_visit_at)}
                      </>
                    ) : null}
                    {lastRefreshedAt ? (
                      <>
                        {" "}
                        · refreshed {lastRefreshedAt.toLocaleTimeString()}
                      </>
                    ) : null}
                  </span>
                ) : null}
                <button
                  type="button"
                  className="vi-refresh-btn vi-refresh-btn-ghost"
                  onClick={() => {
                    setShowRelinkPanel(true);
                    setError("");
                    runAutoMatch({ forceRelink: true });
                  }}
                  disabled={dataLoading || matchLoading}
                >
                  Change link
                </button>
                <button
                  type="button"
                  className="vi-refresh-btn"
                  onClick={() => loadSnapshot({ refresh: true })}
                  disabled={dataLoading}
                >
                  <RefreshCw size={14} className={dataLoading ? "vi-spin" : ""} />
                  Refresh
                </button>
              </div>
            </div>
          ) : null}

          {dataLoading && !snapshot ? (
            <div className="vi-loading">
              <Loader2 size={18} className="vi-spin" />
              Loading care reports…
            </div>
          ) : null}

          {(!clientLinked || showRelinkPanel) ? (
            <div className="vi-match-panel">
              {showRelinkPanel && clientLinked ? (
                <button
                  type="button"
                  className="vi-refresh-btn vi-refresh-btn-ghost"
                  style={{ marginBottom: 12 }}
                  onClick={() => setShowRelinkPanel(false)}
                >
                  ← Back to reports
                </button>
              ) : null}
              <strong>
                {showRelinkPanel
                  ? "Choose a different Vital Insights profile"
                  : "Match this client to Vital Insights"}
              </strong>
              <p className="vi-muted">
                {showRelinkPanel ? (
                  <>
                    Pick the VI loved-one that has the care reports you want (check date of birth).
                    For Bill Hess, choose DOB <strong>10/18/1936</strong> for Dave&apos;s current visits,
                    not 1944.
                  </>
                ) : clientName ? (
                  <>
                    We match by name and date of birth.{" "}
                    <strong>{clientName}</strong> in MyAlbert.ai must link to the same
                    person in your VI agency roster.
                  </>
                ) : (
                  "We match by name and date of birth. This Albert client must link to the same person in your VI agency roster."
                )}
              </p>
              {showMatchCandidates ? (
                <div className="vi-candidates">
                  <p>
                    {matchCandidates.length > 1
                      ? "Multiple VI clients found — pick the correct one:"
                      : "We found a possible match but couldn't auto-link (often date of birth). Confirm and link:"}
                  </p>
                  {matchCandidates.map((candidate, index) => {
                    const candidateId = resolveCandidateId(candidate) || `candidate-${index}`;
                    const isLinking = linkingCandidateId === candidateId;
                    return (
                      <button
                        key={candidateId}
                        type="button"
                        className={`vi-candidate-btn${isLinking ? " vi-candidate-btn-busy" : ""}`}
                        onClick={() => pickCandidate(candidate)}
                        disabled={matchBusy}
                      >
                        {isLinking ? (
                          <>
                            <Loader2 size={14} className="vi-spin" />
                            Linking…
                          </>
                        ) : (
                          <>
                            Confirm link → {candidate.name}
                            {candidate.date_of_birth
                              ? ` · DOB ${new Date(candidate.date_of_birth).toLocaleDateString()}`
                              : ""}
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <button
                  type="button"
                  className="vi-match-btn"
                  onClick={runAutoMatch}
                  disabled={matchBusy}
                >
                  {matchBusy ? (
                    <>
                      <Loader2 size={14} className="vi-spin" />
                      Searching Vital Insights…
                    </>
                  ) : (
                    <>Find {clientName || "this client"} in Vital Insights</>
                  )}
                </button>
              )}
              {matchMessage ? (
                <p className="vi-match-hint">{matchMessage}</p>
              ) : null}
              {error && (!clientLinked || showRelinkPanel) ? (
                <p className="vi-error vi-error-inline">{error}</p>
              ) : null}
            </div>
          ) : null}

          {clientLinked && !showRelinkPanel && activeTab === "latest" ? (
            <div className="vi-latest-layout">
              <div className="vi-section-head">
                <h3>Latest report</h3>
                {latestVisit ? (
                  <span className="vi-section-meta">
                    {formatVisitDate(latestVisit.visit_date)}
                  </span>
                ) : null}
              </div>
              <ReportCard visit={latestVisit} />
            </div>
          ) : null}

          {clientLinked && !showRelinkPanel && activeTab === "archive" ? (
            <div className="vi-archive-layout">
              <aside className="vi-archive-sidebar">
                <div className="vi-section-head">
                  <h3>Archive</h3>
                  <span className="vi-section-meta">
                    {visits.length} report{visits.length === 1 ? "" : "s"}
                  </span>
                </div>
                <ArchiveList
                  visits={visits}
                  selectedVisitId={selectedVisitId}
                  latestVisitId={latestVisitId}
                  onSelect={setSelectedVisitId}
                />
              </aside>
              <div className="vi-archive-detail">
                {chartData.length ? (
                  <div className="vi-chart-card">
                    <div className="vi-chart-head">
                      <strong>Wellness trend</strong>
                      <span>Score across recent reports</span>
                    </div>
                    <div className="vi-chart-wrap">
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ece7e1" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} width={32} />
                          <Tooltip />
                          <Line
                            type="monotone"
                            dataKey="score"
                            stroke="#e8522a"
                            strokeWidth={2.5}
                            dot={{ r: 4, fill: "#e8522a" }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : null}
                <div className="vi-section-head vi-section-head-inline">
                  <h3>Report detail</h3>
                  {selectedVisit ? (
                    <span className="vi-section-meta">
                      {formatVisitDate(selectedVisit.visit_date)}
                    </span>
                  ) : null}
                </div>
                <ReportCard visit={selectedVisit} emptyLabel="Select a report from the archive." />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {error && clientLinked && !showRelinkPanel ? (
        <p className="vi-error">{error}</p>
      ) : null}
    </div>
  );
}
